import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { getRegistrationSessionByToken } from '../../lib/services/registrationSession';
import { NotionPasswordStrength } from '../../components/ui/notion/NotionPasswordStrength';
import { useExitWarning } from '../../lib/hooks';
import { useRegistrationContext } from '../../lib/hooks/useRegistrationContext';
import { RegistrationReferralBanner } from '../../components/registration/RegistrationReferralBanner';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import { REGISTER_VISUALS } from '../../components/register/registerVisuals';

// Messaggio leggibile da un errore di supabase.functions.invoke(): il motivo vero e' nel
// corpo della risposta; se la richiesta non parte affatto (rete) il testo di supabase-js e'
// in inglese ("Failed to send a request to the Edge Function"), quindi lo traduciamo.
async function passwordInvokeErrorMessage(invokeError: any): Promise<string> {
  const fallback = "Errore durante la creazione dell'account. Riprova tra qualche istante.";
  if (invokeError?.context && typeof invokeError.context.json === 'function') {
    try {
      const body = await invokeError.context.json();
      if (body?.code === 'already_registered' && typeof body.error === 'string') return body.error;
      if (typeof body?.error === 'string' && body.error !== 'Errore interno del server') return body.error;
    } catch {
      // corpo non JSON: si usa il messaggio generico
    }
  }
  if (invokeError?.name === 'FunctionsFetchError' || /failed to send|fetch/i.test(String(invokeError?.message ?? ''))) {
    return 'Connessione non riuscita. Controlla la rete e riprova.';
  }
  return fallback;
}

export function PasswordSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionToken = searchParams.get('token');

  const { referrerName, referrerType, trialDays } = useRegistrationContext({ sessionToken });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // Dopo il primo clic su "Continua" i requisiti mancanti restano elencati e si aggiornano
  // mentre si scrive (prima il pulsante restava grigio senza spiegare cosa mancava: 06/10/2026).
  const [attempted, setAttempted] = useState(false);

  // Warning on page exit during registration
  useExitWarning(true, 'Sei sicuro di voler abbandonare la registrazione?');

  useEffect(() => { trackRegisterStepViewed('password'); }, []);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/register');
      return;
    }
  }, [sessionToken, navigate]);

  const validatePassword = (pwd: string): string[] => {
    const validationErrors: string[] = [];

    if (pwd.length < 8) validationErrors.push('Almeno 8 caratteri');
    if (!/[A-Z]/.test(pwd)) validationErrors.push('Almeno una lettera maiuscola');
    if (!/[a-z]/.test(pwd)) validationErrors.push('Almeno una lettera minuscola');
    if (!/[0-9]/.test(pwd)) validationErrors.push('Almeno un numero');

    return validationErrors;
  };

  const collectFormErrors = (pwd: string, confirm: string): string[] => {
    const formErrors = validatePassword(pwd);
    if (!confirm) formErrors.push('Ripeti la password nel campo "Conferma password"');
    else if (pwd !== confirm) formErrors.push('Le due password non sono uguali');
    return formErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setAttempted(true);

    const formErrors = collectFormErrors(password, confirmPassword);
    if (formErrors.length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const { error } = await supabase.functions.invoke('register-password', {
        body: {
          sessionToken,
          password
        }
      });

      if (error) {
        throw new Error(await passwordInvokeErrorMessage(error));
      }

      // NOVITÀ: auto-login client-side. La edge crea l'utente admin-side senza
      // sessione; da qui in poi il wizard gira autenticato e a fine flusso si
      // atterra in dashboard senza rimbalzo su /login.
      let loggedIn = false;
      try {
        const { data: sess } = await getRegistrationSessionByToken(sessionToken);
        if (sess?.email) {
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: sess.email,
            password,
          });
          loggedIn = !signInError;
          if (signInError) console.warn('[PasswordSetup] auto-login fallito:', signInError.message);
        }
      } catch (e) {
        console.warn('[PasswordSetup] auto-login skipped:', e);
      }

      if (loggedIn) {
        navigate(`/register/profile?token=${sessionToken}`);
      } else {
        // Fallback flusso storico: si prosegue anonimi via token (profilo saltato,
        // completabile in app) e a fine flusso si passa dal login.
        navigate(`/register/company?token=${sessionToken}`);
      }

    } catch (error) {
      console.error('Password setup error:', error);
      setErrors([error instanceof Error ? error.message : "Errore durante la creazione dell'account"]);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!sessionToken) {
    return null;
  }

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const liveFormErrors = collectFormErrors(password, confirmPassword);
  const shownErrors = attempted && liveFormErrors.length > 0 ? liveFormErrors : errors;

  return (
    <RegisterSplitLayout visual={REGISTER_VISUALS.password} step={3}>
      <RegistrationReferralBanner
        referrerName={referrerName}
        referrerType={referrerType}
        trialDays={trialDays}
      />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">
          Imposta la tua password
        </h1>
        <p className="text-[#787774] text-sm">
          Scegli una password sicura per accedere al tuo account
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Password Input */}
        <div className="space-y-1.5">
          <label htmlFor="register-new-password" className="block text-sm font-medium text-[#37352F]">
            Password
          </label>
          <div className="relative">
            <input
              id="register-new-password"
              name="new-password"
              autoComplete="new-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="notion-typing-input w-full py-2.5 pl-3 pr-10 text-sm"
              placeholder="Inserisci la tua password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#787774] hover:text-[#37352F] transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Requisiti sempre visibili, anche prima di scrivere */}
        <NotionPasswordStrength password={password} />

        {/* Confirm Password Input */}
        <div className="space-y-1.5">
          <label htmlFor="register-confirm-password" className="block text-sm font-medium text-[#37352F]">
            Conferma password
          </label>
          <div className="relative">
            <input
              id="register-confirm-password"
              name="confirm-password"
              autoComplete="new-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`notion-typing-input w-full py-2.5 pl-3 pr-10 text-sm ${
                confirmPassword && !passwordsMatch ? '!border-red-500' : ''
              } ${passwordsMatch ? '!border-[#0F7B6C]' : ''}`}
              placeholder="Conferma la tua password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#787774] hover:text-[#37352F] transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {/* Password match indicator */}
          {confirmPassword && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 mt-1.5"
            >
              {passwordsMatch ? (
                <>
                  <div className="w-4 h-4 rounded-full bg-[rgba(15,123,108,0.1)] flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-[#0F7B6C]" />
                  </div>
                  <span className="text-xs text-[#0F7B6C]">Le password corrispondono</span>
                </>
              ) : (
                <>
                  <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                    <X className="w-2.5 h-2.5 text-red-500" />
                  </div>
                  <span className="text-xs text-red-500">Le password non corrispondono</span>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Errors */}
        {shownErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-3"
          >
            <div className="flex items-center gap-2 text-red-800 text-sm">
              <X className="w-4 h-4" />
              <span className="font-medium">
                {attempted && liveFormErrors.length > 0 ? 'Per continuare manca ancora:' : 'Non è stato possibile continuare:'}
              </span>
            </div>
            <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
              {shownErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-[rgba(55,53,47,0.09)]">
          <button
            type="button"
            onClick={() => navigate('/register')}
            className="notion-register-btn-ghost inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="notion-register-btn"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Salvataggio...</span>
              </>
            ) : (
              <>
                <span>Continua</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </RegisterSplitLayout>
  );
}
